// Development finance results table rows. Pure functions (results.test.ts).
import { escapeHtml, money } from '../format';
import type { DevResultRow } from '../types';

const TAG_LABELS: Record<string, [string, string]> = {
  lowest_total_cost: ['Lowest total cost', 'tag tag-best'],
  lowest_rate: ['Lowest rate', 'tag tag-outline'],
};
const INTEREST_TYPE_LABELS: Record<string, string> = { rolled_up: 'Rolled up', retained: 'Retained', serviced: 'Serviced' };

export type DevSortBy = 'totalCost' | 'rate';

/** 'totalCost' keeps the server's order (cheapest first). */
export function sortDevResults(results: DevResultRow[], sortBy: DevSortBy): DevResultRow[] {
  const arr = results.slice();
  if (sortBy === 'rate') arr.sort((a, b) => a.rateMonthlyPct - b.rateMonthlyPct);
  return arr;
}

export function devLowestTotalCostGbp(results: DevResultRow[]): number | null {
  const costs = results.map((r) => Math.round(r.totalCostGbp));
  return costs.length > 0 ? Math.min(...costs) : null;
}

export function devResultsHeaderHtml(): string {
  const tip = (text: string, about: string) => `<button type="button" class="info-tip" data-tip="${escapeHtml(text)}" aria-label="More about ${about}">?</button>`;
  return `
      <span>Lender / Product</span>
      <span>Rate pm${tip('This is the estimated interest rate, at the next stage we will confirm this and may be able to negotiate a reduced rate.', 'rate pm')}</span>
      <span>Facility</span>
      <span>Max LTC / LTGDV${tip("The lender's maximum loan to cost and loan to gross development value. The lower of the two usually sets how much you can borrow.", 'max LTC and LTGDV')}</span>
      <span>Arrangement fee</span>
      <span>Approx total cost${tip('This estimate covers your main borrowing costs, including interest and the arrangement fee. An adviser will confirm any remaining fees at the next step.', 'approx total cost')}</span>
      <span></span>`;
}

export function devResultRowHtml(r: DevResultRow, rowIndex: number, isBest: boolean): string {
  const tagInfo = r.tag && r.tag !== 'lowest_total_cost' ? TAG_LABELS[r.tag] : null;
  const detailsId = `result-details-${rowIndex}`;
  const badges = [...(r.lenderTags || []), ...(r.productTags || [])];
  const code = escapeHtml(r.code);
  return `
      <li class="result${isBest ? ' is-best' : ''}">
        <div class="result-grid">
          <div class="cell cell-lender">
            <div class="badges">${isBest ? `<span class="${TAG_LABELS.lowest_total_cost[1]}">${TAG_LABELS.lowest_total_cost[0]}</span>` : ''}${tagInfo ? `<span class="${tagInfo[1]}">${tagInfo[0]}</span>` : ''}</div>
            <span class="lender-name">${escapeHtml(r.lenderName || r.code)}</span>
            <span class="product-name">${escapeHtml(r.productName || 'Development finance product')}</span>
            ${(r.referralNotes || []).map((n) => `<span class="referral-note">${escapeHtml(n)}</span>`).join('')}
            ${badges.length > 0 ? `<div class="badges">${badges.map((t) => `<span class="tag tag-neutral">${escapeHtml(t)}</span>`).join('')}</div>` : ''}
          </div>
          <div class="cell cell-rate"><span class="cell-label">Rate pm</span><span class="cell-value rate-value">${r.rateMonthlyPct.toFixed(2)}%</span></div>
          <div class="cell"><span class="cell-label">Facility</span><span class="cell-value">${money(r.facilityGbp)}</span></div>
          <div class="cell"><span class="cell-label">Max LTC / LTGDV</span><span class="cell-value">${r.maxLtcPct}% / ${r.maxLtgdvPct}%</span></div>
          <div class="cell"><span class="cell-label">Arrangement fee</span><span class="cell-value">${r.arrangementFeePct > 0 ? r.arrangementFeePct.toFixed(1) + '% · ' : ''}${money(r.arrangementFeeAmountGbp)}</span></div>
          <div class="cell cell-total"><span class="cell-label">Approx total cost</span><span class="cell-value">${money(r.totalCostGbp)}</span></div>
          <div class="cell cell-action">
            <button type="button" class="btn ${isBest ? 'btn-primary' : 'btn-outline'}" data-apply="${code}" aria-label="Get exact terms from ${escapeHtml(r.lenderName || r.code)}">Get exact terms</button>
          </div>
        </div>
        <button type="button" class="details-toggle" data-toggle-details="${code}" aria-expanded="false" aria-controls="${detailsId}">Details ▾</button>
        <div class="popdown-wrap" id="${detailsId}">
          <div class="popdown-inner">
            <div class="details-grid">
              <div><span class="k">Exit fee</span>${r.exitFeePct > 0 ? r.exitFeePct.toFixed(1) + '% · ' : ''}${money(r.exitFeeAmountGbp)} (of ${r.exitFeeBasis === 'gdv' ? 'GDV' : 'facility'})</div>
              <div><span class="k">Interest basis</span>${escapeHtml(INTEREST_TYPE_LABELS[r.interestType] || r.interestType)}</div>
            </div>
          </div>
        </div>
      </li>`;
}
